import { LogOut, Moon, Monitor, PanelLeftOpen, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { AppShell, PageHeader } from "@/components/AppShell";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { ApiKeysSection } from "@/components/ApiKeysSection";
import { useLogout } from "@/hooks/useLogout";

const THEMES = [
	{ value: "light", label: "Light", Icon: Sun },
	{ value: "dark", label: "Dark", Icon: Moon },
	{ value: "system", label: "System", Icon: Monitor },
] as const;

export default function SettingsPage() {
	const logout = useLogout();
	const { theme, setTheme } = useTheme();

	return (
		<AppShell>
			<PageHeader>
				<SidebarTrigger className="data-[state=open]:hidden text-primary">
					<PanelLeftOpen className="h-4 w-4" />
				</SidebarTrigger>
				<div className="flex flex-col">
					<h1 className="text-lg font-semibold leading-tight">Settings</h1>
					<p className="text-xs text-muted-foreground">Manage your account.</p>
				</div>
			</PageHeader>

			<div className="flex-1 min-h-0 overflow-y-auto px-6 py-8">
				<div className="max-w-3xl mx-auto space-y-12">
					<ApiKeysSection />

					<section>
						<h2 className="text-lg font-semibold mb-1">Appearance</h2>
						<p className="text-sm text-muted-foreground mb-4">
							Choose how Conbersa looks.
						</p>
						<div className="flex items-center gap-2">
							{THEMES.map(({ value, label, Icon }) => (
								<Button
									key={value}
									size="sm"
									variant={theme === value ? "default" : "outline"}
									onClick={() => setTheme(value)}
								>
									<Icon className="h-4 w-4 mr-1.5" />
									{label}
								</Button>
							))}
						</div>
					</section>

					<section>
						<h2 className="text-lg font-semibold mb-1">Account</h2>
						<p className="text-sm text-muted-foreground mb-4">
							Sign out of Conbersa on this device.
						</p>
						<Button
							variant="outline"
							className="text-destructive border-destructive/40"
							onClick={() => logout.mutate()}
							disabled={logout.isPending}
						>
							<LogOut className="h-4 w-4 mr-2" />
							{logout.isPending ? 'Logging out…' : 'Log out'}
						</Button>
					</section>
				</div>
			</div>
		</AppShell>
	);
}
